import BaseStore from '../base/BaseStore';
import { ActionTypes } from './ActionTypes';
import { HierarchyActionTypes } from './HierarchyActionTypes';
import { ActionTypes as BaseActionTypes } from '../base/BaseActionTypes';
import { search, traverse, sort, parents, EmptyFilter, clone } from 'utilities/hierarchy';
import { guid } from 'utilities/guid';
import { AssetLevels, AssetTypes } from '../constants';

export const StoreEvents = {
    AlarmInactive: 'APP_ALARM_INACTIVE',
    EventsReceived: 'APP_EVENTS_RECEIVED',
    EventsUpdated: 'APP_EVENTS_UPDATED',
    HierarchyFiltered: 'APP_EVENTS_HIERARCHY_FILTERED',
    RunningStatusUpdated: 'AE_RS_UPDATED',
    TransitionTo: 'APP_EVENT_TRANSITION_TO',
    WellRenamed: 'APP_EVENT_WELL_RENAMED'
};

export class Store extends BaseStore {

    state: IAppStoreState;

    constructor() {
        super();

        this.state = {
            loading: false,
            bootstrap: {
                message: '',
                step: null,
                totalSteps: null,
                complete: false
            },
            events: {},
            runningStatus: {},
            hierarchy: [],
            selectedWell: null,
            selectedWells: {},
            hierarchyToggle: true,
            user: null,
            alert: null,
            confirm: null,
            dialog: null,
            systemMessage: null,
            toasts: [],
            watchlists: [],
            wellControllers: {},
        };

        // hack
        this.handlers['ALARMS_ACKNOWLEDGE'] = (action: IAction<IAlarmManagerItem[]>) => {
            const events = { ...this.state.events };
            const acknowledged = action.data;
            let updated = false;

            acknowledged.forEach(alarm => {
                const key = `${alarm.key.EventType}|${alarm.transactionType}|${alarm.transactionProperty}`;
                const existing: AlarmOccurredResponse = events[alarm.wellId][key] ? { ...events[alarm.wellId][key] } : null;

                if (existing) {
                    log('ALARMS: ', `Alarm ${key} acknowledged.`);
                    updated = true;
                    existing.EventAttributes.Acked = new Date();
                    existing.EventAttributes.AckedBy = 'Pending';
                } else {
                    log('ALARMS: ', `Alarm ${key} not found in cache.`);
                }
            });

            if (updated) {
                this.state.events = events;
                this.emitChange();
            }
        }

        this.handlers[HierarchyActionTypes.CollapseAll] = this.collapseAll;
        this.handlers[HierarchyActionTypes.SelectWell] = this.selectWell;
        this.handlers[HierarchyActionTypes.SelectWells] = this.selectWells;
        this.handlers[HierarchyActionTypes.UnselectWell] = this.unselectWell;
        this.handlers[HierarchyActionTypes.UnselectWells] = this.unselectWells;
        this.handlers[ActionTypes.HierarchyFilter] = this.hierarchyFilter;
        this.handlers[ActionTypes.HierarchyFilterClear] = this.hierarchyFilterClear;
        this.handlers[ActionTypes.HierarchyLoaded] = this.hierarchyLoaded;
        this.handlers[HierarchyActionTypes.HierarchySearch] = this.hierarchySearch;
        this.handlers[ActionTypes.HierarchyUpdated] = this.hierarchyUpdated;
        this.handlers[ActionTypes.NodeMoved] = this.nodeMoved;
        this.handlers[ActionTypes.RunningStatusReceived] = this.onRunningStatusReceived;
        this.handlers[ActionTypes.Authenticated] = this.onAuthenticated;
        this.handlers[ActionTypes.BootstrapMessage] = this.bootstrapMessage;
        this.handlers[ActionTypes.EventsChange] = this.changeEvents;
        this.handlers[ActionTypes.EventsReset] = events => this.changeEvents(events, true);
        this.handlers[ActionTypes.UpdateUser] = this.updateUser;
        this.handlers[BaseActionTypes.Alert] = this.onAlert;
        this.handlers[BaseActionTypes.Confirm] = this.onConfirm;
        this.handlers[BaseActionTypes.CloseToast] = this.closeToast;
        this.handlers[BaseActionTypes.Dialog] = this.onDialog;
        this.handlers[BaseActionTypes.ExpandNode] = this.expandNode;
        this.handlers[BaseActionTypes.Loading] = this.loading;
        this.handlers[BaseActionTypes.Toast] = this.onToast;
        this.handlers[BaseActionTypes.TransitionTo] = this.transitionTo;
        this.handlers[ActionTypes.WatchListCreated] = this.watchListCreated;
        this.handlers[ActionTypes.WatchListDeleted] = this.watchListDeleted;
        this.handlers[ActionTypes.WatchListsLoaded] = this.watchListsLoaded;
        this.handlers[ActionTypes.ZediMigrationComplete] = this.zediMigrationComplete;
        // server notifications
        this.handlers[ActionTypes.ServerEventAlertSent] = this.onServerEventAlertSent;
        this.handlers[ActionTypes.ServerHierarchyAdded] = this.onServerHierarchyAdded;
        this.handlers[ActionTypes.ServerHierarchyDeleted] = this.onServerHierarchyDeleted;
        this.handlers[ActionTypes.ServerHierarchyMoved] = this.onServerHierarchyMoved;
        this.handlers[ActionTypes.ServerHierarchyUpdated] = this.onServerHierarchyUpdated;
        this.handlers[ActionTypes.ServerSystemMessage] = this.onServerSystemMessage;
    }

    public getNode = (itemId: string): Hierarchy.IHierarchyItem => {
        return this.state.hierarchyMap[itemId];
    }

    public getSelectedItems = (): Hierarchy.IHierarchyItem[] => {
        let result: Hierarchy.IHierarchyItem[] = [];

        for (let key in this.state.hierarchyMap) {
            const item = this.state.hierarchyMap[key];
            if (item.selected) {
                result.push(item);
            }
        }

        return result;
    }

    public getSelectedWellIds = () => {
        return this.getSelectedWells().map(x => x.id);
    }

    public getSelectedWells = (): Hierarchy.IHierarchyItem[] => {
        return this.getSelectedItems().filter(x => x.isWell);
    }

    private onAuthenticated = (action: IAction<ICurrentUser>) => {
        this.state.user = action.data;
        this.emitChange();
    }

    private bootstrapMessage = (action: IAction<{ message: string, step: number, totalSteps: number, complete: boolean }>) => {
        this.state.bootstrap = action.data;
        this.emitChange();
    }

    private collapseAll = () => {
        const selected: Hierarchy.IHierarchyItem[] = [];

        for (let itemId in this.state.hierarchyMap) {
            const item = this.state.hierarchyMap[itemId];
            if (item.selected) {
                selected.push(item);
            } else {
                item.expanded = false;
            }
        }

        selected.forEach(x => {
            parents(x, (y) => y.expanded = true);
            traverse([x], (y) => {
                y.expanded = true;
            });
        });

        this.state.hierarchy = [...this.state.hierarchy];
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();
    }

    private changeEvents = (action: IAction<AlarmOccurredResponse[]>, withReset: boolean = false) => {

        const storeEvents = withReset ? {} : { ...this.state.events };

        // sort by receive time - if we have an alarm and we also have it's inactive notification,
        // things will cancel out
        const alarms: AlarmOccurredResponse[] = this.extractAllAlarms(action)
            .sort((a, b) => {
                const date1 = a.EventAttributes.ReceiveTime;
                const date2 = b.EventAttributes.ReceiveTime;
                return date1.getTime() > date2.getTime() ? 1 : -1;
            });

        const inactive: AlarmOccurredResponse[] = [];
        const received: AlarmOccurredResponse[] = [];

        alarms.forEach(alarm => {
            const itemId = alarm.Key.ItemId;
            const key = `${alarm.Key.EventType}|${alarm.Key.TransactionType}|${alarm.Key.TransactionProperty}`;

            storeEvents[itemId] = storeEvents[itemId] || {};

            if (alarm.State && alarm.State === 'Inactive') {
                const cleared = storeEvents[itemId][key];

                if (cleared) {
                    inactive.push(cleared);
                    storeEvents[itemId][key] = null;
                    delete storeEvents[itemId][key];
                }

            } else {
                storeEvents[itemId][key] = alarm;

                if (alarm.Key.TransactionProperty === 'RTU_STATUS') {
                    var children = alarm.Key.ChildNodeKeys || [];
                    children.forEach(child => this.buildWellControllerMap(child.ItemId, alarm.Key.ItemId));
                }

                received.push(alarm);
            }
        });

        this.state.events = storeEvents;

        if (inactive.length) {
            this.emit(StoreEvents.AlarmInactive, inactive);
        }

        // NO emitChange HERE!!
        // emit a specific event so that only interested parties
        // receive the notification:
        // 
        // to subscribe: Store.addListener(StoreEvents.EventsReceived, callback)
        // DO NOT FORGET to unsubscribe
        this.emit(StoreEvents.EventsReceived, received);

        this.emit(StoreEvents.EventsUpdated);
    }

    //private onRunningStatusReceived = (action: IAction<RunningStatusResponse[]>) => {
    //    const statuses = action.data;
    //    const runningStatus = { ...this.state.runningStatus };

    //    statuses.forEach(status => {
    //        if (status.stopped) {
    //            runningStatus[status.itemId] = status;
    //        } else {
    //            delete runningStatus[status.itemId];
    //        }
    //    });

    //    this.state.runningStatus = runningStatus;

    //    this.emitChange();
    //}
    private onRunningStatusReceived = (action: IAction<RunningStatusResponse[]>) => {
        const statuses = action.data;
        const runningStatus = { ...this.state.runningStatus };

        const running: RunningStatusResponse[] = [];
        const stopped: RunningStatusResponse[] = [];

        statuses.forEach(status => {
            if (status.stopped) {
                runningStatus[status.itemId] = status;
                stopped.push(status);
            } else {
                running.push(status);
                delete runningStatus[status.itemId];
            }
        });

        this.state.runningStatus = runningStatus;

        //console.log('emitting running status changes', running);
        //console.log('emitting running status changes', stopped);
        this.emit(StoreEvents.RunningStatusUpdated, runningStatus);
        //running.forEach(i => this.emit(StoreEvents.RunningStatusUpdated, i.itemId, true));
        //stopped.forEach(i => this.emit(StoreEvents.RunningStatusUpdated, i.itemId, false));
    }

    private extractAllAlarms(action: IAction<AlarmOccurredResponse[]>) {
        return (action.data || [])
            .reduce((list, item) => {
                let items = [item];
                if (item.Attachments?.length) {
                    items.push(...item.Attachments.map(itemId => ({ ...item, Key: { ...item.Key, ItemId: itemId } })));
                }
                list.push(...items);
                return list;
            }, [] as AlarmOccurredResponse[]);
    }

    private buildWellControllerMap(wellId: string, controllerId: string) {
        const wellControllers = this.state.wellControllers;
        let controllers = wellControllers[wellId];
        if (!controllers) {
            controllers = [];
        }
        if (!controllers.includes(controllerId)) {
            controllers.push(controllerId);
        }
        wellControllers[wellId] = controllers;
    }

    private hierarchySearch = (action: IAction<string>) => {
        const term = action.data;
        const hierarchy = [...this.state.hierarchy];

        search(term, hierarchy);

        this.state.hierarchyFilter = term;
        this.state.hierarchyMap = { ...this.state.hierarchyMap };
        this.state.hierarchy = hierarchy;
        this.state.hierarchyToggle = !this.state.hierarchyToggle;

        this.emitChange();
    }

    private hierarchyFilter = (action: IAction<{ filter: Hierarchy.Filter, result: Hierarchy.FilterResult }>) => {

        const { filter, result } = action.data;

        this.state.filter = filter;
        this.state.hierarchy = result.hierarchy;
        this.state.hierarchyMap = result.hierarchyMap;

        if (result.selectedWells) {
            this.state.selectedWells = result.selectedWells;

            // need to emit filter message first
            this.emit(StoreEvents.HierarchyFiltered, {
                selected: this.state.selectedWells,
                unselected: result.unselectedWells
            });
        }

        this.emitChange();
    }

    private hierarchyFilterClear = () => {
        const hierarchy = [...this.state.initialHierarchy];
        const selectedWells = { ...this.state.selectedWells };
        const hierarchyMap = {};

        traverse(hierarchy, node => {
            node.visible = true;
            node.expanded = false;
            node.selected = false;
            hierarchyMap[node.id] = node;
        });

        // need to emit filter message first
        this.emit(StoreEvents.HierarchyFiltered, {
            selected: {},
            unselected: selectedWells
        });

        this.state.filter = EmptyFilter;
        this.state.selectedWells = {};
        this.state.hierarchy = hierarchy;
        this.state.hierarchyMap = hierarchyMap;

        this.emitChange();
    }

    private hierarchyLoaded = (action: IAction<Hierarchy.IHierarchyItem[]>) => {
        const hierarchy = action.data;

        this.state.hierarchy = hierarchy;
        this.state.hierarchyMap = {};

        traverse(hierarchy, (item: Hierarchy.IHierarchyItem) => {
            this.state.hierarchyMap[item.id] = item;
        });

        this.state.initialHierarchy = clone(hierarchy, null, false);
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();
    }

    private hierarchyUpdated = () => {
        this.state.hierarchy = [...this.state.hierarchy];
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();
    }

    private selectWell = (action: IAction<string>) => {
        const selectedWellId = action.data;
        const hierarchyMap = { ...this.state.hierarchyMap };
        const selectedWell = hierarchyMap[selectedWellId];

        this.state.selectedWell = selectedWell;

        if (Object.values(this.state.selectedWells).length === 0) {
            this.state.selectedWells = {};
            selectedWell.selected = true;
            this.state.selectedWells[selectedWellId] = selectedWell;
        }

        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.state.hierarchyMap = hierarchyMap;

        this.emitChange();
    }

    private unselectWell = (action: IAction<string>) => {
        this.state.hierarchyMap = { ...this.state.hierarchyMap };
        this.state.hierarchyMap[action.data].selected = false;
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();
    }

    private selectWells = (action: IAction<string[]>) => {
        const itemIds = action.data;
        const selectedWells = { ...this.state.selectedWells };

        itemIds.forEach(itemId => {
            const well = this.state.hierarchyMap[itemId];

            if (well) {
                selectedWells[itemId] = well;
                well.selected = true;
            }
        });

        if (Object.keys(selectedWells).length === 1) {
            // if there is only 1 well selected in a multi-select situation
            // make selectedWell the same
            this.state.selectedWell = Object.values(selectedWells)[0];
        } else {
            // clear single selection well
            this.state.selectedWell = null;
        }
        
        this.state.selectedWells = selectedWells;
        this.state.hierarchyToggle = !this.state.hierarchyToggle;

        this.emitChange();
    }

    private unselectWells = (action: IAction<string[]>) => {
        const hierarchyMap = { ...this.state.hierarchyMap };
        let selectedWells: HierarchyMap = {};

        if (action.data) {
            const itemIds = action.data;
            selectedWells = { ...this.state.selectedWells };

            itemIds.forEach(itemId => {
                delete selectedWells[itemId];
                hierarchyMap[itemId].selected = false;
            });

            this.state.selectedWells = selectedWells;

        } else {
            // unselect all
            const nodes = Object.values(hierarchyMap);

            nodes.forEach(node => {
                node.selected = false;
            });

            this.state.selectedWells = {};
        }

        if (Object.keys(selectedWells).length === 1) {
            // if there is only 1 well selected in a multi-select situation
            // make selectedWell the same
            this.state.selectedWell = Object.values(selectedWells)[0];
        } else {
            // clear single selection well
            this.state.selectedWell = null;
        }

        this.state.hierarchyMap = hierarchyMap;
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();
    }

    private updateUser = (action: IAction<ICurrentUser>) => {
        let user = action.data;
        let unitsChanged = user.uomSet !== this.state.user.uomSet;

        this.state.user = user;

        if (unitsChanged) {
            this.state.events = {};
            this.emit(StoreEvents.EventsReceived);
        }

        this.emitChange();
    }

    private onAlert = (action: IAction<IAlertDialogOptions>) => {
        let options = action.data;

        let onClose = options.onClose || function () { };

        options.onClose = () => {
            onClose();
            this.state.alert = null;
            this.emitChange();
        };

        this.state.alert = options;
        this.emitChange();

    }

    private onConfirm = (action: IAction<IConfirmDialogOptions>) => {
        let confirm = action.data;
        let onClose = confirm.onClose || function () { };

        confirm.onClose = () => {
            onClose();
            this.state.confirm = null;
            this.emitChange();
        };

        this.state.confirm = confirm;
        this.emitChange();
    }

    private closeToast = (action: IAction<IToast>) => {
        this.deleteToast(action.data);
    }

    private loading = (action: IAction<boolean>) => {
        this.state.loading = action.data;
        this.emitChange();
    }

    private onDialog = (action: IAction<IDialogOptions>) => {
        let dialog = action.data;
        let onClose = dialog.onClose || function () { };

        dialog.onClose = () => {
            onClose();
            this.state.dialog = null;
            this.emitChange();
        };

        this.state.dialog = dialog;
        this.emitChange();
    }

    private expandNode = (action: IAction<string>) => {
        const itemId = action.data;
        const node = this.state.hierarchyMap[itemId];

        if (!node) {
            return;
        }

        node.expanded = true;

        traverse([node], item => {
            item.expanded = true;
        });

        parents(node, parent => {
            parent.expanded = true;
        });

        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.state.hierarchyMap = { ...this.state.hierarchyMap };
        this.emitChange();
    }

    private deleteToast(toast: IToast) {
        this.state.toasts = this.state.toasts.filter(x => x.id !== toast.id);
        this.emitChange();
    }

    private onToast = (action: IAction<IToast>) => {
        const toast: IToast = action.data;
        this.toast(toast);
    }

    private toast = (toast: IToast) => {
        // display max of 5 toasts
        const toasts = [...this.state.toasts].slice(0, 4);
        toasts.push(toast);

        this.state.toasts = toasts;

        this.emitChange();

        if (toast.duration >= 0) {
            setTimeout(() => {
                this.deleteToast(toast);
            }, toast.duration);
        }
    }

    private transitionTo = (action: IAction<string>) => {
        this.emit(StoreEvents.TransitionTo, action.data);
    }

    private watchListCreated = (action: IAction<WatchList>) => {
        const watchlists = [...this.state.watchlists];
        watchlists.push(action.data);
        this.state.watchlists = watchlists.sort((a, b) => a.description > b.description ? 1 : -1);
        this.emitChange();
    }

    private watchListDeleted = (action: IAction<WatchList>) => {
        const watchlists = this.state.watchlists.filter(x => x.id !== action.data.id);
        this.state.watchlists = watchlists;
        this.emitChange();
    }

    private watchListsLoaded = (action: IAction<WatchList[]>) => {
        this.state.watchlists = action.data;
        this.emitChange();
    }

    private zediMigrationComplete = (action: IAction<string>) => {
        log('Zedi Migration Complete');
        const itemId = action.data;
        const node = this.state.hierarchyMap[itemId];

        if (node) {
            node.locationId = null;
        }

        this.state.hierarchy = [...this.state.hierarchy];
        this.emitChange();
    }

    public nodeMoved = (action: IAction<{ source: Hierarchy.IHierarchyItem, target: Hierarchy.IHierarchyItem }>) => {
        const map = this.state.hierarchyMap;
        const source = map[action.data.source.id];
        const parent = action.data.target;
        const target = map[parent.id];

        if (!source || !target || !map) {
            return;
        }

        source.parent.children = source.parent.children.filter(x => x.id !== source.id);
        source.parent = target;
        target.children.push(source);
        target.children = target.children.sort(sort);

        const snode = action.data.source;
        const tnode = action.data.target;

        snode.parent.children = snode.parent.children.filter(x => x !== snode);
        snode.parent = tnode;
        tnode.children.push(snode);
        tnode.children = tnode.children.sort(sort);

        parent.expanded = true;

        this.state.hierarchy = [...this.state.hierarchy];
        this.state.hierarchyToggle = !this.state.hierarchyToggle;

        this.emitChange();
    }

    // server handlers

    private onServerEventAlertSent = (action: IAction<{success: boolean, message: string, event: Events.IEvent}>) => {

        const { success, message, event } = action.data;

        const toast: IToast = {
            id: guid(),
            duration: 5000,
            message,
            type: success ? 'success' : 'error'
        };

        this.toast(toast);
    }

    // node added
    private onServerHierarchyAdded = (action: IAction<{ branch: Hierarchy.IHierarchyItem, parentId: string }>) => {
        const { parentId, branch: node } = action.data;

        const add = (hierarchy: Hierarchy.IHierarchyItem[], hierarchyMap: HierarchyMap, parentId: string, node: Hierarchy.IHierarchyItem) => {
            // set client side only properties on incoming node
            node.level = AssetLevels[node.category];
            node.isWell = node.category === AssetTypes.Completion;

            const sortByName = (a: Hierarchy.IHierarchyItem, b: Hierarchy.IHierarchyItem) => a.name > b.name ? 1 : -1;

            if (!node || hierarchyMap[node.id]) {
                return;
            }

            if (parentId) {

                const parent = hierarchyMap[parentId];

                if (!parent) {
                    // user does not have parent in their hierarchy
                    return;
                }

                // add node to parent
                parent.children.push(node);

                // sort nodes
                parent.children = parent.children.sort(sortByName);

                // reset node parent to instance already in hierarchy
                node.parent = parent;

                // expand nodes
                parent.expanded = true;

            } else {
                hierarchy.push(node);
                hierarchy = hierarchy.sort(sortByName);
            }

            node.expanded = true;

            // add new node to the hierarchy map
            hierarchyMap[node.id] = node;
        }

        const hierarchy = [...this.state.hierarchy];
        const hierarchyMap = { ...this.state.hierarchyMap };

        add(hierarchy, hierarchyMap, parentId, { ...node });

        this.state.hierarchy = hierarchy;
        this.state.hierarchyMap = hierarchyMap;
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();

        //initial hierarchy
        const rootMap = {};
        const initHierarchy = [...this.state.initialHierarchy];
        traverse(initHierarchy, node => rootMap[node.id] = node);

        add(initHierarchy, rootMap, parentId, { ...node });

        this.state.initialHierarchy = initHierarchy;
    }

    // node moved
    private onServerHierarchyMoved = (action: IAction<{ branch: Hierarchy.IHierarchyItem, parentId: string }>) => {
        const { parentId, branch } = action.data;

        const move = (hierarchyMap: HierarchyMap, parentId: string, branch: Hierarchy.IHierarchyItem) => {
            const existing = hierarchyMap[branch.id];
            if (existing?.parent) {
                const parent = existing.parent;
                parent.children = parent.children.filter(x => x.id !== branch.id);
                delete hierarchyMap[branch.id];
            }

            const target = hierarchyMap[parentId];
            if (target) {
                const node = existing || branch;
                target.expanded = true;
                node.parent = target;
                target.children.push(node);
                hierarchyMap[node.id] = node;
            }
        }

        const hierarchyMap = { ...this.state.hierarchyMap };

        move(hierarchyMap, parentId, { ...branch });

        this.state.hierarchyMap = hierarchyMap;
        this.state.hierarchy = [...this.state.hierarchy];
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();

        //initial hierarchy
        const rootMap = {};
        traverse(this.state.initialHierarchy, node => rootMap[node.id] = node);

        move(rootMap, parentId, { ...branch });

        this.state.initialHierarchy = [...this.state.initialHierarchy];
    }

    // node updated
    private onServerHierarchyUpdated = (action: IAction<Hierarchy.IHierarchyItem>) => {
        const update = (hierarchyMap: HierarchyMap, updated: Hierarchy.IHierarchyItem): boolean => {
            const node = hierarchyMap[updated.id];

            if (!node) {
                return false;
            }

            if (node.name === updated.name
                && node.transStatus === updated.transStatus
                && node.serviceLevel === updated.serviceLevel
                && node.locationId === updated.locationId) {
                return false;
            }

            node.name = updated.name;
            node.transStatus = updated.transStatus;
            node.serviceLevel = updated.serviceLevel;
            node.locationId = updated.locationId;

            return node.expanded || (node.parent && node.parent.expanded);
        }

        // only do notification if node is currently available in the hierarchy
        if (update(this.state.hierarchyMap, { ...action.data })) {
            this.state.hierarchy = [...this.state.hierarchy];
            this.state.hierarchyToggle = !this.state.hierarchyToggle;
            this.emitChange();
        }

        //initial hierarchy
        const rootMap = {};
        traverse(this.state.initialHierarchy, node => rootMap[node.id] = node);

        update(rootMap, { ...action.data });

        this.state.initialHierarchy = [...this.state.initialHierarchy];
    }

    // node deleted
    private onServerHierarchyDeleted = (action: IAction<{ id: string }>) => {
        const { id } = action.data;

        const del = (hierarchy: Hierarchy.IHierarchyItem[], hierarchyMap: HierarchyMap) => {
            const node = hierarchyMap[id];

            if (!node) {
                return;
            }

            const parent = node.parent;

            if (parent && node.category !== AssetTypes.Client) {
                parent.children = parent.children.filter(x => x.id !== node.id);
            } else {
                hierarchy = hierarchy.filter(x => x.id !== node.id)
            }

            delete hierarchyMap[id];
        }

        const hierarchyMap = { ...this.state.hierarchyMap };
        const hierarchy = [...this.state.hierarchy];

        del(hierarchy, hierarchyMap);

        this.state.hierarchyMap = hierarchyMap;
        this.state.hierarchy = hierarchy;
        this.state.hierarchyToggle = !this.state.hierarchyToggle;
        this.emitChange();

        //initial hierarchy
        const rootMap = {};
        const initHierarchy = [...this.state.initialHierarchy];
        traverse(initHierarchy, node => rootMap[node.id] = node);

        del(initHierarchy, rootMap);

        this.state.initialHierarchy = initHierarchy;
    }

    private onServerSystemMessage = (action: IAction<{ title: string, message: string, linkReleaseNotes: boolean }>) => {

        const onClose = () => {
            this.state.systemMessage = null;
            this.emitChange();
        }

        const { title, message, linkReleaseNotes } = action.data;

        this.state.systemMessage = { title, message, linkReleaseNotes, onClose };

        this.emitChange();
    }

}

export default new Store();

